import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { CircleAlert, Zap, Timer, ArrowRight, Flame } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const getRarity = (id: number) => {
  const rarities = [{
    label: 'MYTHIC',
    classes: 'border-fuchsia-500/50 text-fuchsia-400 bg-fuchsia-950/20 shadow-[0_0_15px_-3px_rgba(217,70,239,0.4)]',
    glow: 'group-hover:shadow-[0_0_30px_-5px_rgba(217,70,239,0.6)]'
  }, {
    label: 'RARE',
    classes: 'border-cyan-500/50 text-cyan-400 bg-cyan-950/20 shadow-[0_0_15px_-3px_rgba(34,211,238,0.4)]',
    glow: 'group-hover:shadow-[0_0_30px_-5px_rgba(34,211,238,0.6)]'
  }, {
    label: 'COMMON',
    classes: 'border-emerald-500/50 text-emerald-400 bg-emerald-950/20 shadow-[0_0_15px_-3px_rgba(52,211,153,0.4)]',
    glow: 'group-hover:shadow-[0_0_30px_-5px_rgba(52,211,153,0.6)]'
  }];
  return rarities[id % 3];
};
const getStock = (id: number) => id % 12 + 2;
function ExclusivesGrid() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[500px] w-full animate-pulse rounded-xl border border-border bg-card/50" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <CircleAlert className="mb-4 h-10 w-10 text-destructive" />
        <p className="text-lg font-medium text-muted-foreground">Unable to load exclusives.</p>
        <Button variant="outline" onClick={refetch} className="mt-4">Retry Connection</Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-20 text-center">
        <p className="text-xl text-muted-foreground">No exclusive drops available at the moment.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const rarity = getRarity(product.id);
      const stockLeft = getStock(product.id);
      return <div key={product.id} data-index={i} className="group relative">
            {}
            <Card className={`relative h-full overflow-hidden border bg-card/40 backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 ${rarity.classes} ${rarity.glow}`}>
              
              {}
              <div className="absolute left-0 top-0 z-20 flex w-full items-start justify-between p-4">
                <Badge variant="outline" className={`border-current px-3 py-1 text-xs font-bold tracking-widest backdrop-blur-md ${rarity.classes.split(' ')[1]}`}>
                  {rarity.label}
                </Badge>
                
                <div className="flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-red-500 backdrop-blur-md border border-red-500/30">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
                  </span>
                  Only {stockLeft} Left
                </div>
              </div>

              {}
              <CardContent className="relative flex aspect-[4/5] items-center justify-center p-6 pt-12">
                {}
                <div className={`absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-[60px] transition-opacity duration-500 group-hover:opacity-40 ${rarity.classes.includes('fuchsia') ? 'bg-fuchsia-500' : rarity.classes.includes('cyan') ? 'bg-cyan-500' : 'bg-emerald-500'}`} />
                
                <Link to={product.link} className="z-10 w-full">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-5deg]" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/10 text-muted-foreground">
                      No Image
                    </div>}
                </Link>
              </CardContent>

              {}
              <CardFooter className="relative z-20 flex flex-col gap-4 border-t border-border/50 bg-card/60 p-6 backdrop-blur-md">
                <div className="w-full space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <Link to={product.link} className="group-hover:text-primary transition-colors">
                      <h3 className="font-sans text-xl font-bold leading-tight tracking-tight text-foreground">
                        {product.title}
                      </h3>
                    </Link>
                    <div className="text-right">
                      <span className="block text-lg font-bold text-primary">
                        ${product.price}
                      </span>
                      {product.discounted_price && <span className="text-sm text-muted-foreground line-through">
                          ${product.discounted_price}
                        </span>}
                    </div>
                  </div>
                  
                  {}
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {product.categories.slice(0, 2).map(cat => <span key={cat.id} className="uppercase tracking-wider">{cat.name}</span>)}
                  </div>
                </div>

                {}
                <div className="flex w-full flex-col gap-3">
                  <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide uppercase shadow-[0_0_15px_rgba(var(--primary),0.4)] transition-all hover:shadow-[0_0_25px_rgba(var(--primary),0.6)]" text="Secure Pair" showQuantity={false} />
                  
                  {product.customFields?.release_date && <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Timer className="h-3 w-3" />
                      <span>Drop Date: {product.customFields.release_date}</span>
                    </div>}
                </div>
              </CardFooter>
            </Card>
          </div>;
    })}
    </div>;
}
export default function CurrentExclusives() {
  const wp_query_params = {
    per_page: 3,
    orderby: 'date',
    order: 'desc',
    status: 'publish'
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="current-exclusives" className="dark bg-background text-foreground relative overflow-hidden py-24">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
      <div className="absolute -left-[10%] top-[20%] h-[500px] w-[500px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute -right-[10%] bottom-[20%] h-[500px] w-[500px] rounded-full bg-secondary/5 blur-[120px] pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4">
        <div className="mb-16 flex flex-col items-end justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-4 flex items-center gap-2 text-primary">
              <Flame className="h-5 w-5 animate-pulse" />
              <span className="text-sm font-bold uppercase tracking-[0.2em]">High Heat Releases</span>
            </div>
            <h2 className="font-sans text-4xl font-black uppercase tracking-tighter text-foreground md:text-6xl lg:text-7xl">
              Current <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-white">Exclusives</span>
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              Secure your piece of history. Limited edition drops with verified rarity levels. 
              Once they're gone, they're gone forever.
            </p>
          </div>
          
          <Link to="/limited-drops">
            <Button variant="outline" size="lg" className="group border-primary/50 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground">
              View All Drops
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="w-full">
          <ExclusivesGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}